import { useEffect, useMemo, useState } from 'react';
import { RuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { P2GText } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GBox } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { getItemValuesForCondition } from './getItemValuesForCondition';
import { P2GTextField } from '@parcel2go/core-ui';
import { generateRuleTitle } from './generateRuleTitle';
import { generateRuleDescription } from './generateRuleDescription';
import CreateRuleOptionLabel from './CreateRuleOptionLabel';
import { P2GCheckBoxLegacy } from '@parcel2go/core-ui';
import { AllowedInlineConditions } from './AllowedInlineConditions';

interface props {
  actions: string[];
  item: SmartSendItemApiModel;
  addRule: (selected: string[], title: string, description: string) => void;
  updateExistingRule: (rule: RuleApiModel, selected: string[], title: string, description: string) => void;
  cancel: () => void;
  rule: RuleApiModel;
}

export default function CreateRuleOptions(props: props) {
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');

  const conditionOptions = useMemo(
    () =>
      AllowedInlineConditions.filter(
        (condition) => !props.actions.includes(condition) && getItemValuesForCondition(props.item, condition).length > 0
      ),
    [props.actions, props.item]
  );

  useEffect(() => {
    let preselectedConditions = [];
    if (props.rule) {
      setRuleDescription(props.rule.Description);
      setRuleName(props.rule.Name);
      preselectedConditions = props.rule?.Conditions.filter((x) => AllowedInlineConditions.includes(x.Field)).map(
        (x) => x.Field
      );
    }
    if (preselectedConditions.length > 0) {
      setSelectedConditions(preselectedConditions);
    } else if (conditionOptions.length === 1) {
      setSelectedConditions(conditionOptions);
    }
  }, [conditionOptions, props.rule]);

  useEffect(() => {
    if (!props.rule) {
      setRuleName(generateRuleTitle(props.item, props.actions, selectedConditions));
      setRuleDescription(generateRuleDescription(props.item, props.actions, selectedConditions));
    }
  }, [selectedConditions, props.rule]);

  if (conditionOptions.length === 0) return null;

  return (
    <P2GBox mt={2} bordered p={2}>
      <P2GBox>
        <P2GGrid container direction="column" spacing={1}>
          {conditionOptions.length > 1 && (
            <>
              <P2GGrid item>
                <P2GText variant="body2" component="p">
                  What would you like this rule to match on?
                </P2GText>
                <P2GText variant="body2" component="p" small>
                  <strong>Note:</strong> This rule will apply when an item matches any of these selections.
                </P2GText>
              </P2GGrid>
              {conditionOptions.map((a) => (
                <P2GGrid item key={a}>
                  <P2GCheckBoxLegacy
                    onChange={(v) =>
                      v
                        ? setSelectedConditions([...selectedConditions, a])
                        : setSelectedConditions(selectedConditions.filter((x) => x !== a))
                    }
                    id={a}
                    label={<CreateRuleOptionLabel condition={a} item={props.item} />}
                    checked={selectedConditions.includes(a)}
                    labelPlacement="end"
                    variant="primary"
                    error={false}
                  />
                </P2GGrid>
              ))}
            </>
          )}
          {(props.rule || selectedConditions.length > 0) && (
            <>
              <P2GGrid item>
                <P2GTextField
                  fullWidth
                  value={ruleName}
                  onChange={(v) => setRuleName(v.currentTarget.value)}
                  label="Rule Name"
                />
              </P2GGrid>
              <P2GGrid item>
                <P2GTextField
                  fullWidth
                  rows={ruleDescription.length > 80 ? 4 : 2}
                  multiline
                  value={ruleDescription}
                  onChange={(v) => setRuleDescription(v.currentTarget.value)}
                  label="Rule Description"
                />
              </P2GGrid>
            </>
          )}
        </P2GGrid>
        <P2GBox mt={2}>
          <P2GGrid container spacing={1}>
            <P2GGrid item>
              <P2GButton
                disabled={!props.rule && selectedConditions.length === 0}
                onClick={() =>
                  props.rule
                    ? props.updateExistingRule(props.rule, selectedConditions, ruleName, ruleDescription)
                    : props.addRule(selectedConditions, ruleName, ruleDescription)
                }
              >
                {props.rule && selectedConditions.length === 0 ? 'Delete' : 'Save'}
              </P2GButton>
            </P2GGrid>
            <P2GGrid item>
              <P2GButton variant="primary" onClick={props.cancel}>
                Cancel
              </P2GButton>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      </P2GBox>
    </P2GBox>
  );
}
